import { Component, OnDestroy, OnInit } from '@angular/core';
import { finalize } from 'rxjs';
import { ClientService } from '@proxy/clients';
import { MySommelierDto } from '@proxy/clients/models';
import { CatalogService } from '@proxy/catalogs';
import { SommelierPublicDto } from '@proxy/catalogs/models';
import { NotificationService } from '../shared/services/notification.service';
import { StorageService } from '../shared/services/storage.service';

@Component({
  selector: 'app-my-sommelier',
  templateUrl: './my-sommelier.component.html',
  standalone: false,
})
export class MySommelierComponent implements OnInit, OnDestroy {
  current: MySommelierDto | null = null;
  currentPhotoUrl: string | null = null;
  isLoading = true;

  showModal = false;
  keyword = '';
  province: string | null = null;
  locality: string | null = null;
  results: SommelierPublicDto[] = [];
  resultPhotos: Record<number, string> = {};
  isSearching = false;
  searchError = false;
  selected: SommelierPublicDto | null = null;
  isSubmitting = false;

  provinceOptions: { value: string; label: string }[] = [];
  localityOptions: { value: string; label: string }[] = [];

  constructor(
    private clientService: ClientService,
    private catalogService: CatalogService,
    private not: NotificationService,
    private storageService: StorageService,
  ) {}

  get currentInitials(): string {
    if (!this.current) return '';
    return `${(this.current.name || '').charAt(0)}${(this.current.surname || '').charAt(0)}`.toUpperCase();
  }

  initials(s: SommelierPublicDto): string {
    return `${(s.name || '').charAt(0)}${(s.surname || '').charAt(0)}`.toUpperCase();
  }

  ngOnInit(): void {
    this.loadCurrent();
  }

  ngOnDestroy(): void {
    this.revokeCurrentPhoto();
    this.revokeResultPhotos();
  }

  private revokeCurrentPhoto(): void {
    if (this.currentPhotoUrl) {
      URL.revokeObjectURL(this.currentPhotoUrl);
      this.currentPhotoUrl = null;
    }
  }

  private revokeResultPhotos(): void {
    Object.values(this.resultPhotos).forEach(url => URL.revokeObjectURL(url));
    this.resultPhotos = {};
  }

  private loadCurrent(): void {
    this.isLoading = true;
    this.clientService
      .getMySommelier()
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: s => {
          this.current = s;
          this.revokeCurrentPhoto();
          if (s?.fileAttachmentId) {
            this.storageService.getImage(s.fileAttachmentId).subscribe({
              next: blob => (this.currentPhotoUrl = URL.createObjectURL(blob)),
              error: () => {},
            });
          }
        },
        error: () => (this.current = null),
      });
  }

  openModal(): void {
    this.showModal = true;
    this.keyword = '';
    this.province = null;
    this.locality = null;
    this.selected = null;
    this.search();
  }

  closeModal(): void {
    this.showModal = false;
  }

  onSearch(term: string): void {
    this.keyword = term;
    this.search();
  }

  onClearSearch(): void {
    this.keyword = '';
    this.search();
  }

  onFilter(): void {
    this.search();
  }

  search(): void {
    this.isSearching = true;
    this.searchError = false;
    this.catalogService
      .searchSommeliers({
        keyword: this.keyword || undefined,
        province: this.province || undefined,
        locality: this.locality || undefined,
        skipCount: 0,
        maxResultCount: 50,
      })
      .pipe(finalize(() => (this.isSearching = false)))
      .subscribe({
        next: res => {
          this.revokeResultPhotos();
          this.results = res.items ?? [];
          this.results.forEach(s => {
            if (s.fileAttachmentId) {
              this.storageService.getImage(s.fileAttachmentId).subscribe({
                next: blob => {
                  // La respuesta puede llegar tarde: solo la usamos si el sommelier
                  // sigue en los resultados actuales (evita pintar fotos viejas).
                  if (this.results.some(r => r.id === s.id)) {
                    this.resultPhotos[s.id] = URL.createObjectURL(blob);
                  }
                },
                error: () => {},
              });
            }
          });
          // Facetas a partir de los resultados activos (mismo criterio que la landing).
          this.provinceOptions = this.distinct(this.results.map(r => r.region)).map(v => ({ value: v, label: v }));
          this.localityOptions = this.distinct(this.results.map(r => r.city)).map(v => ({ value: v, label: v }));
          // Si el seleccionado ya no está en los resultados, se limpia.
          if (this.selected && !this.results.some(r => r.id === this.selected!.id)) {
            this.selected = null;
          }
        },
        error: () => {
          this.searchError = true;
          this.results = [];
        },
      });
  }

  private distinct(values: (string | undefined)[]): string[] {
    return [...new Set(values.filter((v): v is string => !!v && v.trim().length > 0))].sort((a, b) =>
      a.localeCompare(b, 'es'),
    );
  }

  pick(s: SommelierPublicDto): void {
    this.selected = s;
  }

  confirm(): void {
    if (!this.selected || this.isSubmitting) return;

    this.isSubmitting = true;
    this.clientService
      .changeMySommelier(this.selected.id)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: res => {
          // El mensaje "ya es tu sommelier" / errores de validación los muestra el
          // interceptor de ABP (UserFriendlyException), igual que el resto de la app.
          this.not.success('::MySommelier', '::MySommelier:Changed', [res.sommelierName]);
          this.showModal = false;
          this.loadCurrent();
        },
      });
  }
}
